/**
 * 連携用のプログラム (MCP) の版の扱い (mcp-stale-client-and-creditor-tools-20261015)。
 *
 * 職員の PC の `acecollect-mcp.mjs` が古いままだと、新しい道具 (画面の業務操作・債権者の登録など) が
 * 一覧に出ず、AI が「その道具はない」と答えてしまう (2026-10-15、申立担当の PC で発生)。
 * 古い版には案内を出す仕組みが無いため、**職員 API 側で版を受け取って記録し、管理画面で知らせる**。
 *
 * - 直接接続 (旧 login 方式) は `User-Agent: acecollect-mcp/<版>` を送る。
 * - ブラウザ許可方式は中継 (`/ai-connections/proxy`) へ同じ User-Agent を送り、中継が下流の API へ
 *   `x-acecollect-mcp-version` で渡す (下流の User-Agent は応答形式の識別に使う固定値のまま)。
 * - 0.8.0 より前のブラウザ許可方式は版を付けず `acecollect-mcp` だけを送る。これは「0.8.0 より前」と
 *   確定できるので {@link MCP_LEGACY_CLIENT_VERSION} として記録し、古いと扱う。
 *
 * BE (Lambda) と FE (管理画面) と MCP 本体が同じ判定を使う。依存ゼロ・純関数のみ。
 */

/** User-Agent の先頭 (MCP 本体が送る)。 */
export const MCP_USER_AGENT_PRODUCT = 'acecollect-mcp';
/** 中継が下流の API へ渡す User-Agent (応答形式の識別用。認可には使わない)。 */
export const MCP_LINKED_USER_AGENT = 'acecollect-mcp-linked';
/** 中継が下流の API へ版を渡す header (小文字)。 */
export const MCP_CLIENT_VERSION_HEADER = 'x-acecollect-mcp-version';
/** 版を送らない古いプログラム (0.8.0 より前のブラウザ許可方式) を表す記録値。 */
export const MCP_LEGACY_CLIENT_VERSION = 'legacy';

/** 記録する版の形 (x.y.z、各桁は数字のみ・長さ上限つき)。 */
const VERSION_PATTERN = /^\d{1,4}\.\d{1,4}\.\d{1,6}$/;

/** 版の文字列 (x.y.z) として受け付けられるか。 */
export function isMcpVersionString(value: unknown): value is string {
  return typeof value === 'string' && VERSION_PATTERN.test(value);
}

/**
 * User-Agent から MCP の版を取り出す。
 * - `acecollect-mcp/0.7.0` → `0.7.0`
 * - `acecollect-mcp` (版なし) → {@link MCP_LEGACY_CLIENT_VERSION}
 * - それ以外 (ブラウザ・中継の固定値・形の違う版) → null (記録しない)
 */
export function parseMcpClientVersionFromUserAgent(userAgent: unknown): string | null {
  if (typeof userAgent !== 'string') return null;
  const ua = userAgent.trim();
  if (ua === MCP_USER_AGENT_PRODUCT) return MCP_LEGACY_CLIENT_VERSION;
  const prefix = `${MCP_USER_AGENT_PRODUCT}/`;
  if (!ua.startsWith(prefix)) return null;
  const version = ua.slice(prefix.length);
  return isMcpVersionString(version) ? version : null;
}

/** 記録・中継で受け付ける版の値 (x.y.z か legacy)。それ以外は null。 */
export function normalizeMcpClientVersion(value: unknown): string | null {
  if (value === MCP_LEGACY_CLIENT_VERSION) return MCP_LEGACY_CLIENT_VERSION;
  return isMcpVersionString(value) ? value : null;
}

function parts(version: string): readonly number[] {
  return version.split('.').map((p) => Number(p));
}

/**
 * 版を比べる (a < b なら負、等しければ 0、a > b なら正)。
 * legacy はどの x.y.z より古い。どちらも legacy なら 0。
 */
export function compareMcpVersions(a: string, b: string): number {
  if (a === b) return 0;
  if (a === MCP_LEGACY_CLIENT_VERSION) return -1;
  if (b === MCP_LEGACY_CLIENT_VERSION) return 1;
  const pa = parts(a);
  const pb = parts(b);
  for (let i = 0; i < 3; i += 1) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * 記録された版が配信中の版より古いか。記録が無い・形が違うときは false (古いと断定しない)。
 */
export function isMcpClientOutdated(recorded: unknown, current: string): boolean {
  const version = normalizeMcpClientVersion(recorded);
  if (version === null || !isMcpVersionString(current)) return false;
  return compareMcpVersions(version, current) < 0;
}
